import { SaveSigningFieldResponseResponseBody } from 'bff'
import { PDField } from '../../../../stores/pdf.store'
import { useSigning } from '../../Context'
import { CheckboxField } from './Checkbox'
import { EmailField } from './Email'
import { InitialsField } from './Initials'
import { NumberField } from './Number'
import { PersonNameField } from './PersonName'
import { SelectField } from './Select'
import { SignatureDateField } from './SignatureDate'
import { TextField } from './Text'
import { TextareaField } from './Textarea'
import { SignatureField } from './Signature'

const isValidHttpUrl = (value: string) => {
  let url

  try {
    url = new URL(value)
  } catch (_) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

const urlContentToDataUri = (url: string): Promise<string> => {
  return fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve) => {
          let reader = new FileReader()
          reader.onload = function () {
            resolve(this.result as string)
          }
          reader.readAsDataURL(blob)
        })
    )
}

export const buildSigningFieldPath = <
  T extends {
    id: string
  }
>(
  field: T
) => `fields.${field.id}` as const

type SigningFieldPath = ReturnType<typeof buildSigningFieldPath>

export interface SigningFormFieldProps {
  field: PDField
  index: number
  path: SigningFieldPath
  onPersist: (
    value: any
  ) => Promise<Pick<SaveSigningFieldResponseResponseBody, 'dependant_updates'>>
}

export const SigningFormFieldSwitcher = ({
  field,
  index,
}: {
  field: PDField
  index: number
}) => {
  const { remote, setSignatureBase64Cache, signatureBase64Cache } = useSigning()

  const fieldProps: SigningFormFieldProps = {
    field,
    index,
    path: buildSigningFieldPath(field),
    onPersist: async (value) => {
      if ((typeof value === 'string' && !value) || value == null)
        return Promise.resolve({
          dependant_updates: [],
        })

      if (field.id in signatureBase64Cache) {
        console.log('b64 signature cache hit')

        return remote.saveFieldResponse(
          field.id,
          signatureBase64Cache[field.id]
        )
      }

      if (isValidHttpUrl(value)) {
        const dataUri = await urlContentToDataUri(value)

        if (field.type === 'signature') {
          console.log('b64 signature cache miss')
          setSignatureBase64Cache((prev) => ({ ...prev, [field.id]: dataUri }))
        }

        return remote.saveFieldResponse(field.id, dataUri)
      }

      return remote.saveFieldResponse(field.id, value)
    },
  }

  switch (field.type) {
    case 'checkbox':
      return <CheckboxField {...fieldProps} />

    case 'number':
      return <NumberField {...fieldProps} />

    case 'company_name':
    case 'text':
      return <TextField {...fieldProps} />

    case 'signature':
      return (
        <SignatureField key={signatureBase64Cache[field.id]} {...fieldProps} />
      )

    case 'select':
      return <SelectField {...fieldProps} />

    case 'textarea':
      return <TextareaField {...fieldProps} />

    case 'initials':
      return <InitialsField {...fieldProps} />

    case 'signature_date':
      return <SignatureDateField {...fieldProps} />

    case 'person_name':
      return <PersonNameField {...fieldProps} />

    case 'email':
      return <EmailField {...fieldProps} />

    default:
      return <></>
  }
}
